import * as d3 from 'd3';
import { Theme } from '../../../theme';

interface ContestLineProps {
  chartArea: d3.Selection<SVGGElement, unknown, null, undefined>;
  xScale: d3.ScaleTime<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  theme: Theme;
  chartHeight: number;
}

/**
 * Renders the Bitcoin Conference 2026 target date line (April 28, 2026)
 * with pulsing animation and label
 */
export const renderContestTargetLine = ({
  chartArea,
  xScale,
  yScale,
  theme,
  chartHeight
}: ContestLineProps): void => {
  const contestDate = new Date('2026-04-28T23:00:00Z'); // 4PM PT = 23:00 UTC
  const now = new Date();
  
  // Only show if contest is active (before evaluation)
  if (now > contestDate) return;
  
  // Check if date is in visible range
  const xDomain = xScale.domain();
  if (contestDate < xDomain[0] || contestDate > xDomain[1]) return;
  
  const xPos = xScale(contestDate);
  const yDomain = yScale.domain();
  
  // Remove existing contest elements
  chartArea.selectAll('.contest-target-group').remove();
  
  // Create group for contest elements
  const contestGroup = chartArea.append('g')
    .attr('class', 'contest-target-group');
  
  // Add SVG defs for gradient and glow
  let defs = chartArea.select<SVGDefsElement>('defs.contest-defs');
  if (defs.empty()) {
    defs = chartArea.append('defs').attr('class', 'contest-defs');
    
    // Vertical gradient for the line
    const gradient = defs.append('linearGradient')
      .attr('id', 'contest-line-gradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%');
    
    gradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', theme.primary)
      .attr('stop-opacity', 0.1);
    
    gradient.append('stop')
      .attr('offset', '50%')
      .attr('stop-color', theme.primary)
      .attr('stop-opacity', 0.8);
    
    gradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', theme.primary)
      .attr('stop-opacity', 0.1);
    
    // Glow filter
    const filter = defs.append('filter')
      .attr('id', 'contest-glow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');
    
    filter.append('feGaussianBlur')
      .attr('stdDeviation', '3')
      .attr('result', 'coloredBlur');
    
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
  }
  
  // Main vertical line with gradient
  contestGroup.append('line')
    .attr('class', 'contest-line')
    .attr('x1', xPos)
    .attr('y1', 0)
    .attr('x2', xPos)
    .attr('y2', chartHeight)
    .attr('stroke', 'url(#contest-line-gradient)')
    .attr('stroke-width', 3)
    .attr('stroke-dasharray', '8,4')
    .style('filter', 'url(#contest-glow)');
  
  // Pulsing highlight line - FULL HEIGHT
  contestGroup.append('line')
    .attr('class', 'contest-pulse-line')
    .attr('x1', xPos)
    .attr('y1', 0)
    .attr('x2', xPos)
    .attr('y2', chartHeight)
    .attr('stroke', theme.primary)
    .attr('stroke-width', 2)
    .attr('opacity', 0.5)
    .style('animation', 'contestPulse 2s ease-in-out infinite');
  
  // Days until contest calculation
  const daysUntil = Math.ceil((contestDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  // Top label background
  const labelY = 30;
  const labelWidth = 160;
  const labelHeight = 50;
  
  contestGroup.append('rect')
    .attr('x', xPos - labelWidth / 2)
    .attr('y', labelY - 10)
    .attr('width', labelWidth)
    .attr('height', labelHeight)
    .attr('rx', 8)
    .attr('fill', theme.surface)
    .attr('stroke', theme.primary)
    .attr('stroke-width', 2)
    .style('filter', 'url(#contest-glow)');
  
  // Label text - "CONTEST"
  contestGroup.append('text')
    .attr('x', xPos)
    .attr('y', labelY + 8)
    .attr('text-anchor', 'middle')
    .attr('fill', theme.primary)
    .attr('font-size', '12px')
    .attr('font-weight', '700')
    .text('🎯 CONTEST');
  
  // Date text
  contestGroup.append('text')
    .attr('x', xPos)
    .attr('y', labelY + 24)
    .attr('text-anchor', 'middle')
    .attr('fill', theme.text)
    .attr('font-size', '11px')
    .text('April 28, 4PM PT');

  // Countdown badge at bottom
  const badgeY = chartHeight - 40;
  const badgeWidth = 90;
  const badgeHeight = 26;
  
  contestGroup.append('rect')
    .attr('x', xPos - badgeWidth / 2)
    .attr('y', badgeY)
    .attr('width', badgeWidth)
    .attr('height', badgeHeight)
    .attr('rx', 13)
    .attr('fill', theme.primary)
    .style('filter', 'url(#contest-glow)');
  
  contestGroup.append('text')
    .attr('x', xPos)
    .attr('y', badgeY + 17)
    .attr('text-anchor', 'middle')
    .attr('fill', '#000')
    .attr('font-size', '11px')
    .attr('font-weight', '700')
    .text(`${daysUntil}d left`);
};

// Add CSS animation for pulse effect
const addContestAnimationStyles = () => {
  const styleId = 'contest-animation-styles';
  if (document.getElementById(styleId)) return;
  
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes contestPulse {
      0%, 100% { opacity: 0.3; stroke-width: 2px; }
      50% { opacity: 0.8; stroke-width: 4px; }
    }
    @keyframes userTargetPulse {
      0%, 100% { 
        r: 8;
        opacity: 0.9;
        filter: drop-shadow(0 0 6px #4caf50);
      }
      50% { 
        r: 14;
        opacity: 1;
        filter: drop-shadow(0 0 15px #4caf50);
      }
    }
    @keyframes userTargetRing {
      0% { 
        r: 20;
        opacity: 0.8;
        stroke-width: 3;
      }
      100% { 
        r: 35;
        opacity: 0;
        stroke-width: 1;
      }
    }
  `;
  document.head.appendChild(style);
};

// Call this when module loads
addContestAnimationStyles();

interface UserPredictionPointProps {
  chartArea: d3.Selection<SVGGElement, unknown, null, undefined>;
  xScale: d3.ScaleTime<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  theme: Theme;
  chartWidth: number;
  userPrediction: number | null;
  userHandle?: string;
}

/**
 * Renders the user's contest prediction as a pulsating target dot on the chart
 * Placed at the intersection of contest date (x) and predicted price (y)
 */
export const renderUserPredictionPoint = ({
  chartArea,
  xScale,
  yScale,
  theme,
  chartWidth,
  userPrediction,
  userHandle
}: UserPredictionPointProps): void => {
  // Remove existing user prediction elements
  chartArea.selectAll('.user-prediction-group').remove();
  
  if (!userPrediction) return;
  
  // Check if price is in visible range
  const yDomain = yScale.domain();
  if (userPrediction < yDomain[0] || userPrediction > yDomain[1]) return;
  
  const yPos = yScale(userPrediction);
  // Position at current time (right edge of data) - not on contest line
  const now = new Date();
  const xDomain = xScale.domain();
  
  // Place at current time if visible, otherwise at right edge
  let xPos = chartWidth - 80;
  if (now >= xDomain[0] && now <= xDomain[1]) {
    xPos = xScale(now);
  }
  
  // Create group for user prediction elements
  const userGroup = chartArea.append('g')
    .attr('class', 'user-prediction-group');
  
  // Add defs for glow effect
  let defs = chartArea.select<SVGDefsElement>('defs.user-prediction-defs');
  if (defs.empty()) {
    defs = chartArea.append('defs').attr('class', 'user-prediction-defs');
    
    // Glow filter for the dot
    const filter = defs.append('filter')
      .attr('id', 'user-target-glow')
      .attr('x', '-100%')
      .attr('y', '-100%')
      .attr('width', '300%')
      .attr('height', '300%');
    
    filter.append('feGaussianBlur')
      .attr('stdDeviation', '4')
      .attr('result', 'coloredBlur');
    
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
  }
  
  // Expanding ring animation (creates ripple effect)
  userGroup.append('circle')
    .attr('class', 'user-target-ring')
    .attr('cx', xPos)
    .attr('cy', yPos)
    .attr('r', 20)
    .attr('fill', 'none')
    .attr('stroke', '#4caf50')
    .attr('stroke-width', 2)
    .style('animation', 'userTargetRing 2s ease-out infinite');
  
  // Second ring (offset timing)
  userGroup.append('circle')
    .attr('class', 'user-target-ring-2')
    .attr('cx', xPos)
    .attr('cy', yPos)
    .attr('r', 20)
    .attr('fill', 'none')
    .attr('stroke', '#4caf50')
    .attr('stroke-width', 2)
    .style('animation', 'userTargetRing 2s ease-out infinite')
    .style('animation-delay', '1s');
  
  // Main pulsating target dot
  userGroup.append('circle')
    .attr('class', 'user-target-dot')
    .attr('cx', xPos)
    .attr('cy', yPos)
    .attr('r', 10)
    .attr('fill', '#4caf50')
    .style('filter', 'url(#user-target-glow)')
    .style('animation', 'userTargetPulse 2s ease-in-out infinite');
  
  // Inner dot (static)
  userGroup.append('circle')
    .attr('class', 'user-target-center')
    .attr('cx', xPos)
    .attr('cy', yPos)
    .attr('r', 4)
    .attr('fill', '#fff');
  
  // Crosshair lines (subtle)
  const crosshairLength = 25;
  userGroup.append('line')
    .attr('x1', xPos - crosshairLength)
    .attr('y1', yPos)
    .attr('x2', xPos + crosshairLength)
    .attr('y2', yPos)
    .attr('stroke', '#4caf50')
    .attr('stroke-width', 1)
    .attr('opacity', 0.5)
    .attr('stroke-dasharray', '4,4');
  
  userGroup.append('line')
    .attr('x1', xPos)
    .attr('y1', yPos - crosshairLength)
    .attr('x2', xPos)
    .attr('y2', yPos + crosshairLength)
    .attr('stroke', '#4caf50')
    .attr('stroke-width', 1)
    .attr('opacity', 0.5)
    .attr('stroke-dasharray', '4,4');
  
  // Price label (positioned above the dot)
  const labelY = yPos - 45;
  const labelWidth = 110;
  const labelHeight = 36;
  
  userGroup.append('rect')
    .attr('x', xPos - labelWidth / 2)
    .attr('y', labelY)
    .attr('width', labelWidth)
    .attr('height', labelHeight)
    .attr('rx', 6)
    .attr('fill', '#4caf50')
    .style('filter', 'url(#user-target-glow)');
  
  userGroup.append('text')
    .attr('x', xPos)
    .attr('y', labelY + 14)
    .attr('text-anchor', 'middle')
    .attr('fill', '#fff')
    .attr('font-size', '9px')
    .attr('opacity', 0.9)
    .text(userHandle ? `@${userHandle}` : 'Your prediction');
  
  userGroup.append('text')
    .attr('x', xPos)
    .attr('y', labelY + 28)
    .attr('text-anchor', 'middle')
    .attr('fill', '#fff')
    .attr('font-size', '13px')
    .attr('font-weight', '700')
    .text(`🎯 $${userPrediction.toLocaleString()}`);
  
  // Horizontal line to Y-axis with price indicator
  userGroup.append('line')
    .attr('x1', xPos)
    .attr('y1', yPos)
    .attr('x2', 0)
    .attr('y2', yPos)
    .attr('stroke', '#4caf50')
    .attr('stroke-width', 2)
    .attr('opacity', 0.4)
    .attr('stroke-dasharray', '6,4');
  
  // Price label on Y-axis
  userGroup.append('rect')
    .attr('x', -55)
    .attr('y', yPos - 10)
    .attr('width', 55)
    .attr('height', 20)
    .attr('fill', '#4caf50')
    .attr('rx', 3);
  
  userGroup.append('text')
    .attr('x', -28)
    .attr('y', yPos + 4)
    .attr('text-anchor', 'middle')
    .attr('fill', '#fff')
    .attr('font-size', '10px')
    .attr('font-weight', '600')
    .text(`$${(userPrediction / 1000).toFixed(0)}k`);
  
  // Connection line to contest date (April 28) - shows where prediction lands
  const contestDate = new Date('2026-04-28T23:00:00Z');
  if (contestDate >= xDomain[0] && contestDate <= xDomain[1]) {
    const contestX = xScale(contestDate);
    
    // Curved path from current position to contest date
    const midX = (xPos + contestX) / 2;
    const controlY = yPos - 30; // Slight curve upward
    
    userGroup.append('path')
      .attr('d', `M ${xPos} ${yPos} Q ${midX} ${controlY} ${contestX} ${yPos}`)
      .attr('fill', 'none')
      .attr('stroke', '#4caf50')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '8,6')
      .attr('opacity', 0.5);
    
    // Target marker on contest line
    userGroup.append('circle')
      .attr('cx', contestX)
      .attr('cy', yPos)
      .attr('r', 6)
      .attr('fill', '#4caf50')
      .attr('opacity', 0.8)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2);
    
    // Small label at contest intersection
    userGroup.append('text')
      .attr('x', contestX + 12)
      .attr('y', yPos + 4)
      .attr('fill', '#4caf50')
      .attr('font-size', '10px')
      .attr('font-weight', '600')
      .text('←APR 28');
  }
};

// Keep the old name as alias for backwards compatibility
export const renderUserPredictionLine = renderUserPredictionPoint;

// Contest entry interface (shared with PriceChart)
export interface ContestEntry {
  id: number;
  twitter_handle: string;
  display_name?: string;
  profile_image_url?: string;
  price_prediction: number;
  submitted_at: string;
  is_current_user?: boolean;
}

interface ContestEntriesDotsProps {
  chartArea: d3.Selection<SVGGElement, unknown, null, undefined>;
  xScale: d3.ScaleTime<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  theme: Theme;
  entries: ContestEntry[];
  currentUserHandle?: string;
  currentPrice?: number;
}

/**
 * Renders all contest entries as dots along the contest date line
 * Current user's entry is highlighted differently
 * Current leader (closest to live price) gets special golden treatment
 */
export const renderContestEntriesDots = ({
  chartArea,
  xScale,
  yScale,
  theme,
  entries,
  currentUserHandle,
  currentPrice
}: ContestEntriesDotsProps): void => {
  // Remove existing contest entry dots
  chartArea.selectAll('.contest-entries-group').remove();
  
  if (!entries || entries.length === 0) return;
  
  const contestDate = new Date('2026-04-28T23:00:00Z'); // 4PM PT = 23:00 UTC
  const xDomain = xScale.domain();
  const yDomain = yScale.domain();
  
  // Only render if contest date is visible
  if (contestDate < xDomain[0] || contestDate > xDomain[1]) return;
  
  const xPos = xScale(contestDate);
  
  // Determine current leader (closest to current price)
  let leaderHandle: string | null = null;
  if (currentPrice && currentPrice > 0) {
    let minDistance = Infinity;
    entries.forEach(entry => {
      const distance = Math.abs(entry.price_prediction - currentPrice);
      if (distance < minDistance) {
        minDistance = distance;
        leaderHandle = entry.twitter_handle;
      }
    });
  }
  
  // Add CSS keyframes for leader animation if not present
  if (!document.getElementById('contest-leader-animations')) {
    const style = document.createElement('style');
    style.id = 'contest-leader-animations';
    style.textContent = `
      @keyframes leaderPulse {
        0%, 100% { r: 10; opacity: 1; }
        50% { r: 13; opacity: 0.9; }
      }
      @keyframes leaderGlow {
        0% { r: 14; opacity: 0.8; }
        100% { r: 28; opacity: 0; }
      }
      @keyframes leaderSparkle {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.5; transform: scale(1.2); }
      }
      @keyframes crownBob {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-2px); }
      }
      @keyframes entryTwinkle {
        0%, 100% { opacity: 0.7; }
        50% { opacity: 0.9; }
      }
      @keyframes starburstPulse {
        0%, 100% { opacity: 0.15; }
        50% { opacity: 0.25; }
      }
    `;
    document.head.appendChild(style);
  }
  
  // Create group for all entry dots
  const entriesGroup = chartArea.append('g')
    .attr('class', 'contest-entries-group');
  
  // Create a background group for connecting lines (rendered behind dots)
  const connectionsGroup = entriesGroup.append('g')
    .attr('class', 'starburst-connections');
  
  // Sort entries: leader first, then current user, for z-order (rendered last = on top)
  const sortedEntries = [...entries].sort((a, b) => {
    const aIsLeader = a.twitter_handle === leaderHandle;
    const bIsLeader = b.twitter_handle === leaderHandle;
    if (aIsLeader) return 1; // Leader last (on top)
    if (bIsLeader) return -1;
    if (a.is_current_user) return 1; // Current user second to last
    if (b.is_current_user) return -1;
    return 0;
  });
  
  // Dynamic sizing based on entry count - smaller dots as we get more entries
  const entryCount = entries.length;
  const baseDotRadius = entryCount <= 20 ? 6 : 
                        entryCount <= 40 ? 5 : 
                        entryCount <= 70 ? 4 : 
                        entryCount <= 100 ? 3.5 : 3;
  const dotRadius = baseDotRadius;
  
  // Starburst pattern: spread dots in spiral/orbital rings around the vertical line
  // Group entries by price proximity for starburst clusters
  const priceGroups: Map<number, { entries: typeof sortedEntries; yPos: number }> = new Map();
  const groupingThreshold = Math.abs(yDomain[1] - yDomain[0]) * 0.02; // 2% of visible range
  
  sortedEntries.forEach((entry) => {
    const price = entry.price_prediction;
    if (price < yDomain[0] || price > yDomain[1]) return;
    
    const yPos = yScale(price);
    
    // Find existing group close to this price
    let foundGroup = false;
    const groupEntries = Array.from(priceGroups.entries());
    for (const [groupY, group] of groupEntries) {
      if (Math.abs(yPos - groupY) < groupingThreshold * 2) {
        group.entries.push(entry);
        foundGroup = true;
        break;
      }
    }
    
    if (!foundGroup) {
      priceGroups.set(yPos, { entries: [entry], yPos });
    }
  });
  
  // Calculate starburst positions for each group
  const placedPositions: { x: number; y: number }[] = [];
  const minDotSpacing = dotRadius * 2.5; // Minimum distance between any two dots
  
  priceGroups.forEach((group) => {
    const centerY = group.yPos;
    const groupEntries = group.entries;
    
    groupEntries.forEach((entry, indexInGroup) => {
      const price = entry.price_prediction;
      let yPos = yScale(price);
      let xOffset = 0;
      let yOffset = 0;
      
      if (indexInGroup === 0) {
        // First entry in group stays on the line
        xOffset = 0;
      } else {
        // Starburst pattern: spread in orbital rings
        // Ring 1: 6 positions, Ring 2: 12 positions, Ring 3: 18 positions, etc.
        const ringIndex = Math.floor((indexInGroup - 1) / 6); // Which ring (0, 1, 2...)
        const posInRing = (indexInGroup - 1) % 6 + ringIndex * 6; // Position within ring
        const positionsInRing = 6 + ringIndex * 6; // How many positions in this ring
        
        // Ring radius increases with each ring
        const baseRadius = 16 + (entryCount > 50 ? -2 : entryCount > 30 ? 0 : 2);
        const ringRadius = baseRadius + ringIndex * (12 + Math.max(0, 4 - ringIndex));
        
        // Angle: start from right, alternate sides, create starburst effect
        // Use golden angle for pleasing spiral distribution
        const goldenAngle = Math.PI * (3 - Math.sqrt(5)); // ~137.5 degrees
        const baseAngle = indexInGroup * goldenAngle;
        
        // Add slight randomization for organic feel (seeded by entry id for consistency)
        const seed = entry.id || indexInGroup;
        const jitter = ((seed * 9301 + 49297) % 233280) / 233280 * 0.3 - 0.15;
        const angle = baseAngle + jitter;
        
        xOffset = Math.cos(angle) * ringRadius;
        yOffset = Math.sin(angle) * ringRadius * 0.6; // Compress vertically for oval shape
      }
      
      // Final position
      let finalX = xPos + xOffset;
      let finalY = yPos + yOffset;
      
      // Collision avoidance - nudge if too close to existing dot
      let attempts = 0;
      while (attempts < 10) {
        let collision = false;
        for (const placed of placedPositions) {
          const dist = Math.sqrt(Math.pow(finalX - placed.x, 2) + Math.pow(finalY - placed.y, 2));
          if (dist < minDotSpacing) {
            collision = true;
            // Nudge outward from center
            const nudgeAngle = Math.atan2(finalY - centerY, finalX - xPos);
            finalX += Math.cos(nudgeAngle) * 4;
            finalY += Math.sin(nudgeAngle) * 2;
            break;
          }
        }
        if (!collision) break;
        attempts++;
      }
      
      placedPositions.push({ x: finalX, y: finalY });
      
      const dotX = finalX;
      yPos = finalY;
      
      // Draw subtle connection line from dot back to the contest line (starburst rays)
      if (Math.abs(dotX - xPos) > 5) { // Only if dot is offset from center
        connectionsGroup.append('line')
          .attr('x1', xPos)
          .attr('y1', yScale(price)) // Original price position on the line
          .attr('x2', dotX)
          .attr('y2', yPos)
          .attr('stroke', theme.primary)
          .attr('stroke-width', 1)
          .attr('opacity', 0.15)
          .style('animation', 'starburstPulse 3s ease-in-out infinite')
          .style('animation-delay', `${(entry.id || indexInGroup) % 10 * 0.3}s`);
      }
      
      const isCurrentUser = entry.is_current_user || 
      (currentUserHandle && entry.twitter_handle?.toLowerCase() === currentUserHandle.toLowerCase());
    const isLeader = entry.twitter_handle === leaderHandle;
    
    // Entry dot group (for hover effects)
    const dotGroup = entriesGroup.append('g')
      .attr('class', `contest-entry-dot ${isCurrentUser ? 'current-user' : ''} ${isLeader ? 'leader' : ''}`)
      .attr('data-handle', entry.twitter_handle)
      .attr('data-price', price)
      .style('cursor', 'pointer');
    
    if (isLeader) {
      // LEADER: Golden pulsating effect with crown!
      const leaderColor = '#FFD700'; // Gold
      
      // Outer expanding glow ring
      dotGroup.append('circle')
        .attr('class', 'leader-glow')
        .attr('cx', dotX)
        .attr('cy', yPos)
        .attr('r', 14)
        .attr('fill', 'none')
        .attr('stroke', leaderColor)
        .attr('stroke-width', 2)
        .style('animation', 'leaderGlow 2s ease-out infinite');
      
      // Second glow ring (offset)
      dotGroup.append('circle')
        .attr('class', 'leader-glow-2')
        .attr('cx', dotX)
        .attr('cy', yPos)
        .attr('r', 14)
        .attr('fill', 'none')
        .attr('stroke', leaderColor)
        .attr('stroke-width', 2)
        .style('animation', 'leaderGlow 2s ease-out infinite')
        .style('animation-delay', '1s');
      
      // Main pulsating golden dot
      dotGroup.append('circle')
        .attr('class', 'leader-dot')
        .attr('cx', dotX)
        .attr('cy', yPos)
        .attr('r', 10)
        .attr('fill', leaderColor)
        .attr('stroke', '#FFF')
        .attr('stroke-width', 2.5)
        .style('filter', 'drop-shadow(0 0 8px #FFD700) drop-shadow(0 0 15px #FFD700)')
        .style('animation', 'leaderPulse 1.5s ease-in-out infinite');
      
      // Inner white sparkle
      dotGroup.append('circle')
        .attr('cx', dotX)
        .attr('cy', yPos)
        .attr('r', 4)
        .attr('fill', '#fff')
        .style('filter', 'drop-shadow(0 0 3px #fff)');
      
      // Crown emoji above the leader dot
      dotGroup.append('text')
        .attr('x', dotX)
        .attr('y', yPos - 18)
        .attr('text-anchor', 'middle')
        .attr('font-size', '14px')
        .style('animation', 'crownBob 2s ease-in-out infinite')
        .text('👑');
      
      // "LEADING" label
      dotGroup.append('text')
        .attr('x', dotX)
        .attr('y', yPos + 24)
        .attr('text-anchor', 'middle')
        .attr('fill', leaderColor)
        .attr('font-size', '9px')
        .attr('font-weight', '700')
        .style('text-shadow', '0 0 4px rgba(255, 215, 0, 0.8)')
        .text('LEADING');
        
    } else if (isCurrentUser) {
      // Current user: larger, green, pulsing
      dotGroup.append('circle')
        .attr('cx', dotX)
        .attr('cy', yPos)
        .attr('r', dotRadius + 4)
        .attr('fill', '#4caf50')
        .attr('opacity', 0.8)
        .attr('stroke', '#fff')
        .attr('stroke-width', 2)
        .style('filter', 'drop-shadow(0 0 6px #4caf50)');
      
      // Inner white dot
      dotGroup.append('circle')
        .attr('cx', dotX)
        .attr('cy', yPos)
        .attr('r', 3)
        .attr('fill', '#fff');
    } else {
      // Other entries: smaller, themed color with subtle twinkle
      // Outer glow (subtle)
      dotGroup.append('circle')
        .attr('cx', dotX)
        .attr('cy', yPos)
        .attr('r', dotRadius + 2)
        .attr('fill', theme.primary)
        .attr('opacity', 0.2)
        .style('filter', 'blur(2px)');
      
      // Main dot with twinkle animation
      dotGroup.append('circle')
        .attr('cx', dotX)
        .attr('cy', yPos)
        .attr('r', dotRadius)
        .attr('fill', theme.primary)
        .attr('stroke', theme.surface)
        .attr('stroke-width', 1.5)
        .style('animation', 'entryTwinkle 3s ease-in-out infinite')
        .style('animation-delay', `${(entry.id || indexInGroup) % 15 * 0.2}s`);
    }
    
    // Tooltip on hover
    dotGroup
      .on('mouseenter', function() {
        // Remove any existing tooltips
        chartArea.selectAll('.entry-tooltip').remove();
        
        // Create tooltip
        const tooltipGroup = chartArea.append('g')
          .attr('class', 'entry-tooltip');
        
        const tooltipWidth = 130;
        const tooltipHeight = isLeader ? 60 : (isCurrentUser ? 50 : 40);
        let tooltipX = dotX + 15;
        let tooltipY = yPos - tooltipHeight / 2;
        
        // Keep tooltip in bounds
        const chartWidth = parseFloat(chartArea.attr('data-width') || '800');
        if (tooltipX + tooltipWidth > chartWidth) {
          tooltipX = dotX - tooltipWidth - 15;
        }
        
        const borderColor = isLeader ? '#FFD700' : (isCurrentUser ? '#4caf50' : theme.primary);
        
        tooltipGroup.append('rect')
          .attr('x', tooltipX)
          .attr('y', tooltipY)
          .attr('width', tooltipWidth)
          .attr('height', tooltipHeight)
          .attr('rx', 6)
          .attr('fill', theme.surface)
          .attr('stroke', borderColor)
          .attr('stroke-width', 2)
          .style('filter', isLeader ? 'drop-shadow(0 0 8px rgba(255, 215, 0, 0.5))' : 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))');
        
        // Handle
        tooltipGroup.append('text')
          .attr('x', tooltipX + 10)
          .attr('y', tooltipY + 16)
          .attr('fill', theme.text)
          .attr('font-size', '11px')
          .attr('font-weight', '600')
          .text(`@${entry.twitter_handle}`);
        
        // Price
        tooltipGroup.append('text')
          .attr('x', tooltipX + 10)
          .attr('y', tooltipY + 32)
          .attr('fill', borderColor)
          .attr('font-size', '13px')
          .attr('font-weight', '700')
          .text(`$${price.toLocaleString()}`);
        
        if (isLeader) {
          tooltipGroup.append('text')
            .attr('x', tooltipX + 10)
            .attr('y', tooltipY + 48)
            .attr('fill', '#FFD700')
            .attr('font-size', '10px')
            .attr('font-weight', '700')
            .text('👑 Currently Winning!');
        } else if (isCurrentUser) {
          tooltipGroup.append('text')
            .attr('x', tooltipX + 10)
            .attr('y', tooltipY + 46)
            .attr('fill', '#4caf50')
            .attr('font-size', '9px')
            .text('(You)');
        }
        
        // Highlight the dot
        const baseRadius = isLeader ? 10 : (isCurrentUser ? dotRadius + 4 : dotRadius);
        d3.select(this).select('circle:not(.leader-glow):not(.leader-glow-2)')
          .transition()
          .duration(150)
          .attr('r', baseRadius + 3);
      })
      .on('mouseleave', function() {
        // Remove tooltip
        chartArea.selectAll('.entry-tooltip').remove();
        
        // Reset dot size
        const baseRadius = isLeader ? 10 : (isCurrentUser ? dotRadius + 4 : dotRadius);
        d3.select(this).select('circle:not(.leader-glow):not(.leader-glow-2)')
          .transition()
          .duration(150)
          .attr('r', baseRadius);
      });
    }); // End groupEntries.forEach
  }); // End priceGroups.forEach
  
  // Add entry count label near the top of the contest line
  if (entries.length > 0) {
    const labelY = 85; // Below the "CONTEST" label
    entriesGroup.append('text')
      .attr('x', xPos)
      .attr('y', labelY)
      .attr('text-anchor', 'middle')
      .attr('fill', theme.textSecondary)
      .attr('font-size', '10px')
      .text(`${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}`);
  }
};

export default renderContestTargetLine;
